import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { Button } from "antd";
import Form from "antd/es/form";
import { useForm, useWatch } from "antd/es/form/Form";
import Popover from "antd/es/popover";
import Radio from "antd/es/radio";
import { defineMessages, useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Company, Person } from "@mapmycustomers/shared/types/entity";

import {
  CompanyRelationshipField,
  PersonRelationshipField,
} from "@app/component/FormFields/components/Associations";
import RadioGroup from "@app/component/input/RadioGroup";
import RoundButton from "@app/component/RoundButton";
import useView from "@app/component/view/utils/useView";
import ChangeAssociationsPayload from "@app/store/associations/ChangeAssociationsPayload";
import {
  getCurrentUserId,
  isCurrentUserMember,
  isCurrentUserOwner,
  isCurrentUserReadOnly,
} from "@app/store/iam";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import { getEntityTypeDisplayName } from "@app/util/ui";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import commonStyles from "./CommonFilterStyles.module.scss";
import styles from "./RecordFilter.module.scss";

type Record = Company | Person;
const RECORD_ENTITY_TYPES = [EntityType.COMPANY, EntityType.PERSON];

type FormValues = {
  companyId?: Company["id"];
  entityType: (typeof RECORD_ENTITY_TYPES)[number];
  personId?: Person["id"];
};

const messages = defineMessages({
  apply: {
    id: "reports.mileageTracking.filters.record.popover.apply",
    defaultMessage: "Apply",
    description:
      "Apply button in the Popover of the Record filter button on the Mileage Tracking page",
  },
  cancel: {
    id: "reports.mileageTracking.filters.record.popover.cancel",
    defaultMessage: "Cancel",
    description:
      "Cancel button in the Popover of the Record filter button on the Mileage Tracking page",
  },
  clear: {
    id: "reports.mileageTracking.filters.record.popover.clear",
    defaultMessage: "Clear filter",
    description:
      "Clear button in the Popover of the Record filter button on the Mileage Tracking page",
  },
  popoverTitle: {
    id: "reports.mileageTracking.filters.record.popover",
    defaultMessage: "Filter by record",
    description: "Popover title in Record filter button on the Mileage Tracking page",
  },
  title: {
    id: "reports.mileageTracking.filters.record.title",
    defaultMessage: "Record",
    description: "Title of the Record filter button on the Mileage Tracking page",
  },
});

export interface RecordFilterProps {}

const RecordFilter: FC<RecordFilterProps> = () => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const {
    updateFilterModel,
    viewState: { filter },
  } = useView();

  const [popoverVisible, setPopoverVisibility] = useState<boolean>(false);
  const handleHide = useCallback(() => setPopoverVisibility(false), []);

  const [form] = useForm<FormValues>();

  const condition = filter[TripFieldName.RECORD];
  const value = useMemo(
    () =>
      condition && isSimpleCondition(condition) && condition.operator === FilterOperator.EQUALS
        ? (condition.value as Record)
        : undefined,
    [condition]
  );

  const [entity, setEntity] = useState<Record | undefined>(value);
  const handleChangeCompany = useCallback(
    ({ company }: Omit<ChangeAssociationsPayload, "entityType">) => {
      setEntity(company);
      form.setFieldValue("personId", undefined);
    },
    [form]
  );
  const handleChangePerson = useCallback(
    ({ person }: Omit<ChangeAssociationsPayload, "entityType">) => {
      setEntity(person);
      form.setFieldValue("companyId", undefined);
    },
    [form]
  );
  useEffect(() => {
    setEntity(value);
    if (!value) {
      form.setFieldValue("companyId", undefined);
      form.setFieldValue("personId", undefined);
    }
  }, [value, form]);

  const entityType = useWatch("entityType", form) ?? value?.entity ?? EntityType.COMPANY;

  const handleFormFinish = useCallback(() => {
    analytics.using(["Record Filter"]);
    updateFilterModel({
      [TripFieldName.RECORD]: entity
        ? {
            operator: FilterOperator.EQUALS,
            value: {
              id: entity?.id,
              entity: entity?.entity,
              name: entity?.name,
            },
          }
        : undefined,
    });
    handleHide();
  }, [analytics, updateFilterModel, entity, handleHide]);

  const handleClear = useCallback(() => {
    setEntity(undefined);
    form.setFieldValue("companyId", undefined);
    form.setFieldValue("personId", undefined);
    updateFilterModel({
      [TripFieldName.RECORD]: undefined,
    });
  }, [form, updateFilterModel]);

  return (
    <Popover
      content={
        <div>
          <Form<FormValues>
            form={form}
            initialValues={{
              companyId: value?.entity === EntityType.COMPANY ? (value as Company).id : undefined,
              entityType: value?.entity ?? EntityType.COMPANY,
              personId: value?.entity === EntityType.PERSON ? (value as Person).id : undefined,
            }}
            layout="vertical"
            onFinish={handleFormFinish}
          >
            <Form.Item label="Record Type" name="entityType">
              <RadioGroup<EntityType>>
                {RECORD_ENTITY_TYPES.map((entityType) => (
                  <Radio.Button key={entityType} value={entityType}>
                    {getEntityTypeDisplayName(intl, entityType, {
                      lowercase: false,
                      plural: false,
                    })}
                  </Radio.Button>
                ))}
              </RadioGroup>
            </Form.Item>
            {entityType === EntityType.COMPANY && (
              <CompanyRelationshipField
                allowAdd={false}
                allowClear={false}
                associatedCompany={
                  entity?.entity === EntityType.COMPANY ? (entity as Company) : undefined
                }
                className={styles.entityField}
                entityType={EntityType.TRIP}
                label={getEntityTypeDisplayName(intl, entityType, {
                  lowercase: false,
                  plural: false,
                })}
                onChangeAssociatedEntities={handleChangeCompany}
                // to be higher than popover
                zIndex={1031}
              />
            )}
            {entityType === EntityType.PERSON && (
              <PersonRelationshipField
                allowAdd={false}
                allowClear={false}
                associatedPerson={
                  entity?.entity === EntityType.PERSON ? (entity as Person) : undefined
                }
                className={styles.entityField}
                label={getEntityTypeDisplayName(intl, entityType, {
                  lowercase: false,
                  plural: false,
                })}
                onChangeAssociatedEntities={handleChangePerson}
                // to be higher than popover
                zIndex={1031}
              />
            )}
          </Form>
          <div className={styles.footer}>
            <Button onClick={handleHide}>{intl.formatMessage(messages.cancel)}</Button>
            <Button disabled={!entity} onClick={form.submit} type="primary">
              {intl.formatMessage(messages.apply)}
            </Button>
          </div>
        </div>
      }
      destroyTooltipOnHide
      onOpenChange={setPopoverVisibility}
      open={popoverVisible}
      overlayClassName={commonStyles.container}
      placement="bottom"
      showArrow={false}
      title={
        <div className={styles.title}>
          <div>{intl.formatMessage(messages.popoverTitle)}</div>
          <Button
            className={styles.deleteButton}
            disabled={!entity}
            onClick={handleClear}
            type="link"
          >
            {intl.formatMessage(messages.clear)}
          </Button>
        </div>
      }
      trigger={["click"]}
    >
      <RoundButton active={!!value} hasMenu>
        {intl.formatMessage(messages.title)}
      </RoundButton>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  isCurrentUserOwner: isCurrentUserOwner(state),
  isRep: isCurrentUserMember(state) || isCurrentUserReadOnly(state),
  teams: getTeams(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(RecordFilter);
